<template>
    <NavigationWarningDialog
        v-model="showPrompt"
        @on-close="onDialogClosed"
    />
    <div class="operations-administration-page">
        <section class="header">
            <h1>Operations Management</h1>
            <section class="nav-links">
                <div class="page-navigation">
                    <a
                        class="back-link"
                        @click="onBackClicked"
                    >
                        <al-icon icon="inverted-left" />
                        <span>Back</span>
                    </a>
                </div>
            </section>

            <!-- Warning Section: No workstream definitions are enabled -->
            <section
                v-if="isWsDefOperationConfigRefDataEmpty"
                class="info-box-wrapper"
            >
                <al-message
                    type="warning"
                    class="info-box"
                >
                    No workstreams definitions are currently active (enabled). To see your data, go to the Disabled tab
                    below and enable the workstreams associated with the supported integrations.
                </al-message>
            </section>

            <section class="info-box-wrapper">
                <al-message
                    type="info"
                    class="info-box"
                >
                    <div>
                        <strong class="front-matter">Integration Support:</strong> Operations Management dashboards
                        support data from <strong class="supported-integration">Zendesk</strong> and
                        <strong class="supported-integration">Salesforce [Package 5.19.0 or above]</strong>. Other
                        integrations aren't currently supported as they don't provide the full set of information
                        required for accurate dashboard reporting.
                    </div>
                </al-message>
            </section>
            <ErrorMessage :error-detail="errorDetail" />
        </section>
        <section class="tabs-section">
            <p class="front-matter todo">
                Enable a workstream to make it available in Backlog Insights and Workstream Assignments reports.
            </p>

            <div class="tabs">
                <button
                    v-for="state in tabStates"
                    :key="state"
                    :class="{ active: activeTab === state }"
                    @click="activeTab = state"
                >
                    {{ tabTitle(state) }}
                </button>
            </div>
            <KeepAlive>
                <WorkstreamStatusTab
                    state="enabled"
                    v-if="activeTab === 'enabled'"
                    v-model="enabledTabChanges"
                    :data="data"
                    :is-loading="isLoading"
                    data-test-id="status-enabled"
                />
            </KeepAlive>
            <KeepAlive>
                <WorkstreamStatusTab
                    state="disabled"
                    v-if="activeTab === 'disabled'"
                    v-model="disabledTabChanges"
                    :data="data"
                    :is-loading="isLoading"
                />
            </KeepAlive>
        </section>

        <div class="footer">
            <SaveChangesButtons
                :changed="changedRows"
                @on-save="onSave"
                @on-reset="onReset"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    capitalize, computed, ref, watch
} from 'vue';
import WorkstreamStatusTab from '@/pages/OperationsAdministration/components/WorkstreamStatusTab.vue';

import { WorkstreamState } from '@/pages/OperationsAdministration/types/WorkstreamState';
import { useWorkstreamDefinitionOperationConfigData } from '@/pages/OperationsAdministration/composables/useWorkstreamDefinitionOperationConfigData';
import { WorkstreamDefinitionOperationConfigGridRow } from '@/pages/OperationsAdministration/types/WorkstreamDefinitionOperationConfigGridRow';
import NavigationWarningDialog from '@/pages/OperationsAdministration/components/NavigationWarningDialog.vue';
import { useNavigationPrompt } from '@/pages/OperationsAdministration/composables/useNavigationPrompt';
import SaveChangesButtons from '@/pages/OperationsAdministration/components/SaveChangesButtons.vue';
import { useActiveWsDefOperationConfigRefDataStore } from '@/stores/activeWsDefOperationConfigRefDataStore';
import ErrorMessage from '@/pages/OperationsAdministration/components/ErrorMessage.vue';

const { data, isLoading, errorDetail, updateData } = useWorkstreamDefinitionOperationConfigData();

// Check if workstream definitions are empty
const { activeWsDefOperationConfigRefDataStore } = useActiveWsDefOperationConfigRefDataStore();
const isWsDefOperationConfigRefDataEmpty = computed(
    () => activeWsDefOperationConfigRefDataStore.loaded && activeWsDefOperationConfigRefDataStore.data.length === 0
);

// Tab state
const activeTab = ref<WorkstreamState>(WorkstreamState.enabled);
const tabStates = Object.keys(WorkstreamState) as WorkstreamState[];
const isDirty = ref<boolean>(false);
function tabTitle(state: WorkstreamState) {
    return `${capitalize(state)} (${data.value.filter((d) => d.state === state).length})`;
}

const enabledTabChanges = ref<WorkstreamDefinitionOperationConfigGridRow[]>([]);
const disabledTabChanges = ref<WorkstreamDefinitionOperationConfigGridRow[]>([]);
const changedRows = computed(() => [ ...enabledTabChanges.value, ...disabledTabChanges.value ]);

watch([ enabledTabChanges, disabledTabChanges ], () => {
    isDirty.value = enabledTabChanges.value.length !== 0 || disabledTabChanges.value.length !== 0;
});

const { showPrompt, onDialogClosed, onBackClicked } = useNavigationPrompt(isDirty);

function switchTab() {
    return (activeTab.value = activeTab.value === 'enabled' ? 'disabled' : 'enabled');
}

function onSave() {
    updateData(changedRows.value).then(() => switchTab());
}

function onReset() {
    enabledTabChanges.value = [];
    disabledTabChanges.value = [];
}
</script>

<style scoped lang="scss">
@use '@/styles/commonPageStyles.scss';
@use '@/styles/tabs.scss';

.operations-administration-page {
    margin: 1.5rem 2rem;

    display: flex;
    flex-direction: column;

    .header {
        flex: 0;
        h1 {
            font-size: 1.6rem;
        }
        .supported-integration {
            font-weight: bold;
        }

        .nav-links {
            .page-navigation {
                margin-left: 0.3rem;
                display: inline-flex;
                align-items: center;

                a {
                    display: inline-flex;
                    align-items: center;
                    font-size: 1rem;
                    text-decoration: none;
                    padding: 1rem 0; // vertically align center
                    &:link,
                    &:visited,
                    &:hover {
                        color: var(--color-text--80);
                    }
                    al-icon {
                        // icon centre doesn't match text centre
                        font-size: 1.3rem;
                        margin: 0 0.3rem;
                    }
                }
            }
        }
    }
    .tabs-section {
        flex: 1;
        height: 100%;
    }
    .footer {
        flex: 0;
    }
}
</style>

<style lang="scss">
html,
body {
    height: 100vh;
}
</style>